import type { BoxProps, ButtonProps } from '@chakra-ui/react'
import { Avatar, Box, Button, Menu, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import { DisclosureIcon } from './icons'

/**
 * What goes inside the sidebar, dressed for it.
 *
 * The sidebar is dark and sets `sidebar.fg` and `sidebar.hover` for the things
 * that sit on it. A control that takes Chakra's defaults instead renders
 * dark-on-dark and disappears — which is exactly what the admin's first
 * sign-out did, an outline button with invisible text.
 *
 * These live with the shell rather than in either application because the shell
 * is what imposes the surface. An application supplying its own is an
 * application guessing at somebody else's palette, and the two guesses will not
 * match.
 */

// Hoisted: object literals in JSX would be new props on every render.
const itemHover = { bg: 'sidebar.hover' }
const itemSelected = { bg: 'sidebar.hover', fontWeight: 'semibold' }
const buttonHover = { bg: 'sidebar.hover', opacity: 1 }
const triggerHover = { bg: 'sidebar.active' }

// Opens upward: the account area sits at the foot of the sidebar, so a menu
// placed below it would open off the bottom of the viewport.
//
// Positioned against the viewport rather than against an ancestor, which is
// what lets it render inline — see SidebarAccount. An absolutely positioned
// menu would be clipped by the sidebar's own `overflowY: auto`.
const menuAbove = { placement: 'top-start', strategy: 'fixed' } as const

/**
 * Marks the current route. Pass to a router Link's `activeProps`.
 *
 * A data attribute rather than an inline style, so the look stays in the
 * component: Chakra reads `data-selected` as `_selected`, which means the
 * current page is described in tokens like everything else rather than in a
 * hard-coded colour the router happens to carry.
 *
 * The sidebar's own palette already intends this — hover and the current page
 * share `sidebar.hover`, and the reference draws them alike.
 */
export const sidebarActiveProps = { 'data-selected': true }

/**
 * One item of navigation, wrapping whatever link the application uses.
 *
 *   <SidebarItem asChild>
 *     <Link to="/markets" activeProps={sidebarActiveProps}>
 *       <MarketIcon />
 *       Markets
 *     </Link>
 *   </SidebarItem>
 *
 * The link is the application's, because the routes are; the look is not.
 * `asChild` puts the chrome on the anchor itself, so the whole row is the
 * target and the current page can fill it.
 */
export function SidebarItem({ children, ...rest }: BoxProps) {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap="2.5"
      borderRadius="l2"
      px="3"
      py="2"
      color="sidebar.fg"
      fontSize="sm"
      fontWeight="medium"
      letterSpacing="ui"
      _hover={itemHover}
      _selected={itemSelected}
      transition="background 0.12s"
      {...rest}
    >
      {children}
    </Box>
  )
}

/** A control in the sidebar. Same weight as an item of navigation. */
export function SidebarButton({ children, ...rest }: ButtonProps) {
  return (
    <Button
      size="sm"
      variant="ghost"
      w="full"
      justifyContent="flex-start"
      color="sidebar.fg"
      opacity="0.75"
      fontWeight="medium"
      letterSpacing="ui"
      _hover={buttonHover}
      {...rest}
    >
      {children}
    </Button>
  )
}

/**
 * Initials for the avatar.
 *
 * From a person's own name where there is one, falling back to the address the
 * boundary attests. Never the other way round: the boundary attests no name,
 * and what a person calls themselves is the thing they told us.
 *
 * Two letters from two words, one from anything else. An address is reduced to
 * its local part first, so `jess@salesbricks.com` gives J rather than JS — the
 * domain is the company's and says nothing about the person.
 *
 * Both are the caller's strings and neither is guaranteed: a person who has
 * given no name and an operator the boundary attests nothing for both reach
 * here, and an empty circle is a better answer than a crash.
 */
export function initialsOf(name: string, email: string): string {
  if (name.trim() !== '') return lettersOf(name.trim().split(/\s+/u))

  const [local = '', domain = ''] = email.split('@')

  // A local part that separates words gives them: `jess.rose@` is JR. One that
  // does not falls back to the domain, so `jess@lawrence.pm` is JL rather than
  // a lone J — an address is the only thing this person has told us, and two
  // letters from it are more distinguishing than one.
  const parts = local.split(/[._-]+/u).filter((w) => w !== '')
  if (parts.length > 1) return lettersOf(parts)

  return lettersOf([local, domain].filter((w) => w !== ''))
}

/** First letters of the first and last of these, upper case. */
function lettersOf(words: string[]): string {
  if (words.length === 0) return ''
  if (words.length === 1) return (words[0]?.[0] ?? '').toUpperCase()

  return ((words[0]?.[0] ?? '') + (words[words.length - 1]?.[0] ?? '')).toUpperCase()
}

/**
 * One thing you can do from the account menu.
 *
 * `Menu.Item` rather than a button, so the menu owns the keyboard: arrow keys
 * move between items, escape closes, and the trigger takes focus back. A plain
 * button dropped inside the content would look identical and do none of it.
 */
export function SidebarMenuItem({
  value,
  onActivate,
  disabled,
  children,
}: {
  /** Identifies the item to the menu. Required by Chakra, unused by us. */
  value: string
  /**
   * What pressing this does.
   *
   * Named for the act rather than for the event, because the event it is bound
   * to is not the menu's `select` — see below.
   */
  onActivate?: (() => void) | undefined
  disabled?: boolean | undefined
  children: ReactNode
}) {
  return (
    // onClick rather than onSelect.
    //
    // Below `lg` the sidebar is a drawer, and a drawer is modal: it marks the
    // document inert and takes pointer events off the body. A menu nested
    // inside one still opens and still highlights, but its machine does not
    // reach select — so the one control in the account area silently did
    // nothing on a phone. A menu item is a control, and responding to being
    // pressed is not a workaround; the menu still routes Enter and Space here.
    <Menu.Item
      value={value}
      onClick={onActivate}
      disabled={disabled}
      cursor="pointer"
      fontSize="sm"
      letterSpacing="ui"
    >
      {children}
    </Menu.Item>
  )
}

/**
 * The account area at the foot of the sidebar: who you are, and what you can do
 * about it.
 *
 * An avatar and a name that open a menu, rather than a card with the one
 * control it happens to have. There is exactly one item today; the shape is for
 * the second, so that adding settings is adding an item rather than redesigning
 * the foot of the sidebar.
 *
 * It takes the name and the address rather than a label, because the initials
 * need both and the precedence between them is one rule. It used to take a
 * single string, which meant each application collapsed them separately and the
 * console — which has no name to collapse — passed an address.
 *
 */
export function SidebarAccount({
  name,
  email,
  organisation,
  children,
}: {
  /** What this person calls themselves. Empty where they have not said. */
  name?: string | undefined
  /** The address the boundary attests. */
  email: string
  /**
   * What their organisation calls itself. Empty until somebody gives one.
   *
   * Under their own name rather than beside it, because they are two different
   * facts — who you are, and who you are here for. A buyer is about to have
   * Scout write to sellers on that organisation's behalf, and this is the one
   * place that says which one.
   */
  organisation?: string | undefined
  children?: ReactNode
}) {
  // Falling back to `You` rather than to the address: the address is already
  // the initials beside it, and repeating it would make the row say one fact
  // twice where the second line is meant to be the organisation.
  const shown = name !== undefined && name.trim() !== '' ? name.trim() : 'You'
  const under = organisation?.trim() ?? ''

  return (
    <Menu.Root positioning={menuAbove}>
      <Menu.Trigger asChild>
        {/*
          asChild rather than as="button": `as` keeps the div's prop types, so
          `type` would not typecheck and the whole row would be a div pretending
          to be a control.
        */}
        <Box
          asChild
          w="full"
          display="flex"
          alignItems="center"
          gap="2.5"
          bg="sidebar.hover"
          borderRadius="l3"
          p="2.5"
          textAlign="start"
          cursor="pointer"
          _hover={triggerHover}
        >
          <button type="button" aria-label={`Account: ${shown}`}>
            <Avatar.Root size="sm" flexShrink="0" bg="sidebar.solid" color="sidebar.bg">
              <Avatar.Fallback fontSize="xs" fontWeight="semibold">
                {initialsOf(name ?? '', email)}
              </Avatar.Fallback>
            </Avatar.Root>

            <Box flex="1" minW="0">
              <Text fontSize="sm" fontWeight="medium" lineHeight="body" color="sidebar.fg" truncate>
                {shown}
              </Text>
              {/*
                Nothing where the organisation has no name, rather than a
                placeholder. A name is empty until somebody gives one, and a
                line reading "Unnamed" would have Scout inventing a fact about
                the buyer in the one place that says who they are.
              */}
              {under !== '' && (
                <Text fontSize="xs" lineHeight="body" color="sidebar.fg" opacity="0.6" truncate>
                  {under}
                </Text>
              )}
            </Box>

            <Box color="sidebar.fg" opacity="0.5" flexShrink="0" display="flex">
              <DisclosureIcon size="14" />
            </Box>
          </button>
        </Box>
      </Menu.Trigger>

      {/*
        Inline rather than portalled to the body.
        
        Below `lg` the sidebar is a drawer, and a drawer makes everything
        outside itself inert — so a menu portalled to the body would be
        unreachable on a phone, by a pointer and by a screen reader alike. Kept
        inside, it is inside the drawer's focus trap where it belongs. The
        clipping a portal would have avoided is handled by positioning against
        the viewport instead.
      */}
      <Menu.Positioner>
        {/* Its own surface. The menu leaves the dark sidebar, so it takes the
            application's ordinary panel colours rather than the sidebar's. */}
        <Menu.Content minW="44" bg="bg.panel" borderRadius="l2" boxShadow="lg" py="1">
          {children}
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  )
}
